import { useMemo } from "react";
import { IInternalUser, IUserAtom } from "../atoms/UserAtom";

/**
 * Function that given an user and internal user returns
 * the preferred alias for them.
 *
 * If there's only an user, returns `user.emailOptions.aliases[0]`.
 * If there's an internal user, returns the user alias selected for that
 * internal user.
 * @param params
 */
export function useDefaultAlias(params: {
  user: IUserAtom | null;
  internalUser: IInternalUser | null;
}) {
  const { user, internalUser } = params;

  const defaultAlias = useMemo(() => {
    // If I don't have an user, the app is still loading, return null
    if (!user) return null;

    // If I don't have an internal user, it's only a normal user, return the
    // default value for him
    if (!internalUser) return user.emailOptions.aliases[0];

    // If I have both, then check on the internal user map configuration
    // for this specific user

    const internalUserAliasId =
      internalUser.internalUserConfig?.[user.userUid]?.aliasId;

    if (!internalUserAliasId) return user.emailOptions.aliases[0];

    const aliasFromInternalUser = user.emailOptions.aliases.find(
      (alias) => alias.id === internalUserAliasId
    );

    if (!aliasFromInternalUser) return user.emailOptions.aliases[0];

    return aliasFromInternalUser;
  }, [user, internalUser]);

  return { defaultAlias };
}
